import React from "react";
import { useNavigate } from "react-router-dom";
import { X, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { useStore } from "../context/StoreContext";
import { formatPrice, mediaUrl } from "../lib/api";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, removeFromCart, cartSubtotal } = useStore();
  const navigate = useNavigate();
  const go = (to) => { setCartOpen(false); navigate(to); };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="bg-ivory w-full sm:w-[440px] border-linen p-0 flex flex-col" data-testid="cart-drawer">
        <SheetTitle className="sr-only">Shopping Bag</SheetTitle>
        <div className="flex items-center justify-between px-6 h-16 border-b border-linen shrink-0">
          <span className="eyebrow">Shopping Bag ({cart.length})</span>
          <button onClick={() => setCartOpen(false)} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <ShoppingBag className="w-10 h-10 text-stonemuted mb-4" strokeWidth={1} />
            <p className="font-serif text-xl text-pitch">Your bag is empty</p>
            <p className="text-sm text-stonemuted mt-2">Discover one-of-one pieces from the archive.</p>
            <button onClick={() => go("/shop")} className="mt-6 text-[12px] uppercase tracking-[0.2em] border-b border-pitch pb-1">Continue Shopping</button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {cart.map((item) => (
                <div key={item.product_id} className="flex gap-4" data-testid="cart-item">
                  <div className="w-20 h-24 bg-bone shrink-0 overflow-hidden cursor-pointer" onClick={() => go(`/product/${item.snapshot.slug}`)}>
                    {item.snapshot.image && <img src={mediaUrl(item.snapshot.image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono uppercase tracking-widest text-stonemuted">{item.snapshot.brand}</div>
                    <div className="font-serif text-base text-pitch leading-snug">{item.snapshot.title}</div>
                    <div className="text-sm mt-1">{formatPrice(item.snapshot.price, item.snapshot.currency)}</div>
                    <button onClick={() => removeFromCart(item.product_id)} data-testid="cart-item-remove"
                      className="text-[11px] uppercase tracking-widest text-stonemuted hover:text-vermilion mt-2 transition-colors">Remove</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-linen px-6 py-5 shrink-0 space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-stonemuted">Subtotal</span>
                <span className="font-medium">{formatPrice(cartSubtotal)}</span>
              </div>
              <p className="text-xs text-stonemuted">Shipping and any discounts are calculated at checkout.</p>
              <button onClick={() => go("/checkout")} data-testid="cart-checkout-button"
                className="w-full bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] py-4 hover:bg-charcoal transition-colors">
                Proceed to Checkout
              </button>
              <button onClick={() => go("/cart")} className="w-full text-[12px] uppercase tracking-[0.2em] text-charcoal">View Bag</button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
